import React, { useState, useEffect } from 'react';
import { useTurnos } from '../../turnos/hooks/useTurnos';
import { useFiltros } from '../../../shared/hooks/useFiltros';
import { useCatalogos } from '../../calendario/hooks/useCatalogos';
import { useAuth } from '../../../core/context/AuthContext';
import { turnosAPI } from '../../../core/api/api';
import { CalendarMonth } from '@mui/icons-material';
import Header from '../../../shared/components/layout/Header';
import Footer from '../../../shared/components/layout/Footer';
import { Alert } from '../../../shared/components';
import { Toast } from '../../../shared/components/Toast';
import FiltrosBusqueda from '../../../shared/components/FiltrosBusqueda';
import TablaTurnos from './TablaTurnos';
import EditarTurno from './EditarTurno/EditarTurno';
import NotificarModal from './NotificarModal/NotificarModal';
import './GrillaTurnos.css';

const GrillaTurnos = () => {
  const { user } = useAuth();
  const { turnos, loading, error, cargarTodosTurnos, setError, setTurnos } = useTurnos();
  const { filtros, handleFiltroChange, limpiarFiltros } = useFiltros();
  const { centros, tiposEstudios, procesadores, estadosTurno, categorias, obrasSociales, equipos, equiposCPAP } = useCatalogos();
  const [vistaActual, setVistaActual] = useState('grilla');
  const [turnoSeleccionado, setTurnoSeleccionado] = useState(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [mostrarNotificar, setMostrarNotificar] = useState(false);
  const [turnoNotificar, setTurnoNotificar] = useState(null);
  const [toast, setToast] = useState(null);

  const handleBuscar = async () => {
    const data = await cargarTodosTurnos(filtros);
    if (user?.role === 'Tecnico' && user?.idMedico) {
      const turnosFiltrados = data.filter(turno => turno.idMedico === user.idMedico);
       
      setTurnos(turnosFiltrados);
    }
  };

  useEffect(() => {
    handleBuscar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEditarTurno = async (idTurno) => {
    try {
      setScrollPosition(window.pageYOffset);
      const response = await turnosAPI.getTurnoById(idTurno);
      setTurnoSeleccionado(response.data);
      setVistaActual('editar');
      window.scrollTo(0, 0);
    } catch (err) {
      const errorMsg = err.response?.status === 404 
        ? 'El turno no existe o fue eliminado'
        : 'Error al cargar el turno';
      setError(errorMsg);
      console.error('Error al editar turno:', err);
    }
  };

  const handleVolverGrilla = () => {
    setVistaActual('grilla');
    setTurnoSeleccionado(null);
    setTimeout(() => {
      window.scrollTo(0, scrollPosition);
    }, 0);
  };

  const handleGuardarTurno = async () => {
    setVistaActual('grilla');
    setTurnoSeleccionado(null);
    await cargarTodosTurnos(filtros);
    setTimeout(() => {
      window.scrollTo(0, scrollPosition);
    }, 0);
  };

  const handleNuevoTurno = () => {
    setScrollPosition(window.pageYOffset);
    setTurnoSeleccionado(null);
    setVistaActual('editar');
    window.scrollTo(0, 0);
  };

  const handleAccederPortal = async (idTurno) => {
    try {
      const { data } = await turnosAPI.generarAccesoDirecto(idTurno);
      const url = `${process.env.REACT_APP_PORTAL_URL}/acceso-directo?t=${encodeURIComponent(data.token)}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      setToast({ message: 'No se pudo abrir el portal del paciente.', type: 'error' });
    }
  };

  const handleNotificarTurno = async (idTurno) => {
    try {
      const response = await turnosAPI.getTurnoById(idTurno);
      setTurnoNotificar(response.data);
      setMostrarNotificar(true);
    } catch {
      setError('Error al cargar datos del turno para notificar.');
    }
  };

  if (vistaActual === 'editar') {
    return <EditarTurno turno={turnoSeleccionado} onVolver={handleVolverGrilla} onGuardar={handleGuardarTurno} soloLectura={user?.role === 'Tecnico'} />;
  }

  return (
    <div className="grilla-turnos">
      <Header />
      <div className="main-content">
        <div className="page-header">
          <h2 className="page-title-grilla">
            <CalendarMonth sx={{ fontSize: 32, mr: 1, verticalAlign: 'middle', color: '#6d1212' }} />
            Gestión de Turnos
          </h2>
          <span className="page-subtitle">Administración de turnos y citas médicas</span>
        </div>

        <Alert message={error} onClose={() => setError('')} />

        <FiltrosBusqueda
          filtros={filtros}
          onFiltroChange={handleFiltroChange}
          onBuscar={handleBuscar}
          onLimpiar={limpiarFiltros}
          centros={centros}
          tiposEstudios={tiposEstudios}
          estadosTurno={estadosTurno}
          procesadores={procesadores}
          categorias={categorias}
          obrasSociales={obrasSociales}
          equipos={equipos}
          equiposCPAP={equiposCPAP}
          loading={loading}
        />

        <TablaTurnos
          turnos={turnos}
          procesadores={procesadores}
          onEditarTurno={handleEditarTurno}
          onNotificarTurno={handleNotificarTurno}
          onAccederPortal={handleAccederPortal}
          onNuevoTurno={handleNuevoTurno}
          loading={loading}
          ocultarNuevoTurno={user?.role === 'Tecnico'}
          userRole={user?.role}
        />
      </div>
      <Footer />
      <NotificarModal
        show={mostrarNotificar}
        onHide={() => setMostrarNotificar(false)}
        turno={turnoNotificar}
        onResult={(result) => setToast(result)}
      />
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default GrillaTurnos;
